import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { Skill } from "../services/api";

export interface SkillFormValues {
  skill_code: string;
  name: string;
  description?: string;
  group_type?: string;
  group_code?: string;
  tags: string[];
  certs: string[];
  aliases: string[];
  related_skills: string[];
  last_reviewed_utc?: string;
}

interface SkillFormProps {
  initial?: SkillFormValues;
  isSaving?: boolean;
  onSubmit: (values: SkillFormValues) => void;
  onCancel: () => void;
}

const createInitialState = (seed?: SkillFormValues): SkillFormValues => ({
  skill_code: seed?.skill_code ?? "",
  name: seed?.name ?? "",
  description: seed?.description ?? "",
  group_type: seed?.group_type ?? "",
  group_code: seed?.group_code ?? "",
  tags: seed?.tags ?? [],
  certs: seed?.certs ?? [],
  aliases: seed?.aliases ?? [],
  related_skills: seed?.related_skills ?? [],
  last_reviewed_utc: seed?.last_reviewed_utc ?? "",
});

const stringifyList = (list: string[] | undefined): string => (list ?? []).join(", ");

const parseList = (value: string): string[] =>
  value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

export function SkillForm({ initial, isSaving, onSubmit, onCancel }: SkillFormProps) {
  const [form, setForm] = useState<SkillFormValues>(createInitialState(initial));

  useEffect(() => {
    setForm(createInitialState(initial));
  }, [initial]);

  function handleListChange(field: keyof SkillFormValues, value: string) {
    setForm((prev) => ({ ...prev, [field]: parseList(value) }));
  }

  function handleChange(field: keyof SkillFormValues, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit(form);
  }

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <label>
        Skill Code
        <input
          type="text"
          value={form.skill_code}
          required
          disabled={Boolean(initial?.skill_code)}
          onChange={(event) => handleChange("skill_code", event.target.value.toLowerCase())}
        />
      </label>
      <label>
        Name
        <input type="text" value={form.name} required onChange={(event) => handleChange("name", event.target.value)} />
      </label>
      <label className="span-2">
        Description
        <textarea value={form.description ?? ""} rows={3} onChange={(event) => handleChange("description", event.target.value)} />
      </label>
      <label>
        Group Type
        <input type="text" value={form.group_type ?? ""} onChange={(event) => handleChange("group_type", event.target.value)} />
      </label>
      <label>
        Group Code
        <input type="text" value={form.group_code ?? ""} onChange={(event) => handleChange("group_code", event.target.value)} />
      </label>
      <label>
        Last Reviewed (UTC)
        <input type="date" value={form.last_reviewed_utc ?? ""} onChange={(event) => handleChange("last_reviewed_utc", event.target.value)} />
      </label>
      <label className="span-2">
        Tags (comma separated)
        <input type="text" value={stringifyList(form.tags)} onChange={(event) => handleListChange("tags", event.target.value)} />
      </label>
      <label className="span-2">
        Certifications (comma separated)
        <input type="text" value={stringifyList(form.certs)} onChange={(event) => handleListChange("certs", event.target.value)} />
      </label>
      <label className="span-2">
        Aliases (comma separated)
        <input type="text" value={stringifyList(form.aliases)} onChange={(event) => handleListChange("aliases", event.target.value)} />
      </label>
      <label className="span-2">
        Related Skills (comma separated codes)
        <input type="text" value={stringifyList(form.related_skills)} onChange={(event) => handleListChange("related_skills", event.target.value)} />
      </label>
      <div className="form-actions span-2">
        <button type="submit" className="primary" disabled={isSaving}>
          {isSaving ? "Saving…" : initial?.skill_code ? "Update Skill" : "Create Skill"}
        </button>
        <button type="button" className="ghost" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function toFormValues(skill: Skill): SkillFormValues {
  return {
    skill_code: skill.skill_code,
    name: skill.name,
    description: skill.description,
    group_type: skill.group_type,
    group_code: skill.group_code,
    tags: skill.tags ?? [],
    certs: skill.certs ?? [],
    aliases: skill.aliases ?? [],
    related_skills: skill.related_skills ?? [],
    last_reviewed_utc: skill.last_reviewed_utc,
  };
}
